import { published80Interval, servedEconomics } from './servedEconomics.js'

export const PURCHASE_FIELDS = Object.freeze([
  'purchase', 'importCosts', 'discountPct', 'repairs', 'sellingCosts',
  'monthlyHolding', 'months', 'requiredProfit', 'cashBudget',
])

export function purchaseDefaults(car) {
  const economics = servedEconomics(car)
  return Object.fromEntries(PURCHASE_FIELDS.map(key => [key,
    key === 'purchase' && economics.valid ? String(economics.price_amount_eur) :
    key === 'importCosts' && economics.valid ? String(Math.round(economics.non_purchase_costs_incl_vat * 100) / 100) : '',
  ]))
}

/** Immediate screening result from the published gross-cost identities. Extra
 * costs remain unknown: headroom is not cash profit and the low band is no floor.
 * The optional all-in allowance is explicitly the user's, not a model default. */
export function purchaseScreen(car, { allowance = '', discountPct = '0' } = {}) {
  const band = published80Interval(car), economics = servedEconomics(car)
  if (!band || !economics.valid) return null
  const numeric = (raw, max) => raw == null || typeof raw === 'boolean' || String(raw).trim() === '' ||
    !Number.isFinite(Number(raw)) || Number(raw) < 0 || Number(raw) > max ? null : Number(raw)
  const discount = numeric(discountPct, 100), reserve = numeric(allowance, 1e9)
  const landed = economics.total_costs_incl_vat
  const room = band.lower - landed
  const scenarioLow = discount === null ? null : band.lower * (1 - discount / 100)
  const maximumBid = reserve === null || scenarioLow === null ? null : scenarioLow - economics.non_purchase_costs_incl_vat - reserve
  return { purchase: economics.price_amount_eur, importCosts: economics.non_purchase_costs_incl_vat,
    landed, lower: band.lower, room, status: room > 0 ? 'investigate' : 'negotiate',
    allowance: reserve, discountPct: discount, maximumBid,
    reduction: maximumBid === null ? null : Math.max(0, economics.price_amount_eur - maximumBid) }
}

/** A cash scenario, NOT a new valuation or probability of profit. Use gross
 * published costs, never the business VAT toggle or an invented repair reserve.
 * Blank assumptions are unknown, not zero. Negative outcomes stay visible. */
export function purchasePlan(car, inputs) {
  const band = published80Interval(car)
  if (!band || !servedEconomics(car).valid) return null
  const values = {}
  for (const key of PURCHASE_FIELDS) {
    const raw = inputs[key]
    if (raw == null || String(raw).trim() === '' || typeof raw === 'boolean') return null
    const n = Number(raw)
    const max = key === 'months' ? 120 : key === 'discountPct' ? 100 : 1e9
    if (!Number.isFinite(n) || n < 0 || n > max) return null
    values[key] = n
  }
  if (values.purchase <= 0 || values.cashBudget <= 0) return null
  const factor = 1 - values.discountPct / 100
  const exits = { lower: band.lower * factor, point: band.point * factor, upper: band.upper * factor }
  const fixed = values.importCosts + values.repairs + values.sellingCosts
  const costsAt = months => values.purchase + fixed + values.monthlyHolding * months
  const cashRequired = costsAt(values.months)
  const maximumBid = Math.min(
    exits.lower - fixed - values.monthlyHolding * values.months - values.requiredProfit,
    values.cashBudget - fixed - values.monthlyHolding * values.months,
  )
  return {
    inputs: values, exits, cashRequired, maximumBid,
    budgetGap: Math.max(0, cashRequired - values.cashBudget),
    breakEven: cashRequired,
    lowerProfit: exits.lower - cashRequired,
    pointProfit: exits.point - cashRequired,
    upperProfit: exits.upper - cashRequired,
    requiredReduction: Math.max(0, values.purchase - maximumBid),
    meetsScenario: maximumBid >= values.purchase,
    sensitivity: [...new Set([1, 3, 6, values.months])].sort((a, b) => a - b)
      .map(months => ({ months, ...Object.fromEntries(Object.entries(exits).map(([key, exit]) => [key, exit - costsAt(months)])) })),
  }
}

export const PURCHASE_CHECKS = Object.freeze([
  ['identity', 'Seller confirmed availability, gross price, VIN and exact specification'],
  ['condition', 'Independent inspection, damage/history and mileage evidence reviewed'],
  ['costs', 'Written transport quote and vehicle-specific BPM/VAT/registration costs checked'],
  ['exit', 'Comparable NL cars reviewed for condition, trim and private versus dealer pricing'],
  ['liquidity', 'Resale route and affordable holding period chosen without assuming a quick sale'],
])

export function purchaseMemo(car, plan, checks, t = key => key, now = new Date()) {
  const lines = [t('My import plan'), `${car.make || car.brand || ''} ${car.model || ''} ${car.year || ''}`,
    `${t('Listing')}: ${car.listing_id || car.id || ''}`,
    `${t('Source')}: ${car.source_ad_url || car.detail_url || ''}`,
    `${t('Catalogue captured')}: ${car.scraped_at || '?'}`,
    `${t('Source checked')}: ${car.source_checked_at || '?'} (${car.source_flag || '?'})`,
    `${t('Decision snapshot')}: ${car.decision_snapshot_id || '?'}`,
    `${t('Exported')}: ${now.toISOString()}`, '',
    t('User assumptions, not verified quotes. No VAT deduction. Personal tax and your own time are not calculated.'),
    ...Object.entries(plan.inputs).map(([key, value]) => `${key}: ${value}`), '',
    ...['cashRequired', 'breakEven', 'maximumBid', 'lowerProfit', 'pointProfit', 'upperProfit', 'budgetGap'].map(key => `${key}: EUR ${plan[key].toFixed(2)}`), '',
    ...PURCHASE_CHECKS.map(([key, label]) => `${checks[key] ? '[x]' : '[ ]'} ${t(label)}`), '',
    t('A completed checklist is your own record, not AutoMargin approval. The low estimate is not a floor; losses can be larger. No verified sale price or probability of profit.'),
  ]
  return lines.join('\n')
}
